import React, { ReactNode, useEffect, useMemo, useState } from "react"
import qs from "qs"
import { toast } from "react-toastify"
import { useLocation, useSearchParams } from "react-router-dom"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"

import ConfirmConnectionDialog from "../ConfirmConnectionDialog"
import SuccessConnectionDialog from "../SuccessConnectionDialog"
import Sidebar from "./components/Sidebar"

import { useConnect } from "./connect"
import { confirmationDialogPropsMap, ConnectorTypeEnum } from "./utils"
import {
  isAirbyteConnector,
  isIngestionConnector,
} from "~/ui-rtk/utils/connector-utils"
import { useCompanyConnectorControllerCompleteCompanyConnectorOnboardingMutation } from "~/ui-rtk/api/companyConnectorApi"
import {
  isBadRequestError,
  isInternalServerError,
  isUnauthorizedError,
} from "~/ui-rtk/utils/http-utils"
import useLoadingState from "~/ui-rtk/hooks/loading-state"
import { useAppSelector } from "~/ui-rtk/app/hooks"
import SubscribeDialog from "../SubscribeDialog/SubscribeDialog"
import { selectUserSessionRole } from "~/ui-rtk/app/selectors/user.selector"
import { SessionRole } from "~/api/dto/auth/session-role"
import { CompanySetupStatus } from "~/ui-rtk/api/types"
import { CONNECTORS_IDS } from "~/ui-rtk/constants/sources"
import SourceItems from "./components/SourceItems/SourceItems"
import { shouldShowMetaAdvanced } from "./components/MetaAdvancedItem/visibility"
import { DataSyncInfo } from "./components"
import { serviceConnectorMap } from "../forms/utils"
import AdditionalConfigForm from "../forms/AdditionalConfigForm"
import CustomConnectorConfigForm from "../forms/CustomConnectorConfigForm"
import IngestionForm from "../forms/ingestion"
import YotpoAirbyteForm from "../forms/yotpo-airbyte"

// Custom (non-Airbyte) connectors that pop a post-OAuth additional-config form
// via the autoOpen URL param. Empty for now — instagram_insights uses the
// existing ConfirmConnectionDialog (via serviceConnectorMap + stateParam) and
// renders the picker inline there. Add a service id here only if it needs the
// standalone autoOpen path.
const CUSTOM_ADDITIONAL_CONFIG_SERVICES = new Set<string>([])
import { AirbytePollingManager } from "../AirbytePollingManager"
import useIntegrationSetupStatus from "~/ui-rtk/hooks/integration-setup-status"
import { useConnectorsTracking } from "~/ui-rtk/hooks/useConnectorsTracking"

const ConnectorsTable: React.FC = () => {
  const currentCompany = useCurrentCompany()
  const { integrationsReady, message: setupMessage } =
    useIntegrationSetupStatus()

  const [Dialog, setDialog] = useState<ReactNode | null>(null)
  const role = useAppSelector(selectUserSessionRole)
  const isSuperAdmin = role === SessionRole.SUPER_ADMIN

  const [completeAsync, { isError: isCompleteAsyncError }] =
    useCompanyConnectorControllerCompleteCompanyConnectorOnboardingMutation()

  const [searchParams, setSearchParams] = useSearchParams()
  const fivetranId = searchParams.get("id")
  const categoryFromUrl = searchParams.get("category")
  const autoOpenService = searchParams.get("autoOpen")
  const autoOpenCompanyConnectorId = searchParams.get("companyConnectorId")
  const { setIsLoading } = useLoadingState()

  const location = useLocation()
  const params = qs.parse(location.search, { ignoreQueryPrefix: true }) as {
    state?: { companyConnectorId?: string; type?: ConnectorTypeEnum }
  }

  const [stateParam, setStateParam] = useState(params?.state ?? null)
  const [searchTerm, setSearchTerm] = useState("")
  const [activeCategory, setActiveCategory] = useState<string>(
    categoryFromUrl || "Essential",
  )
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false)
  const [isRefetching, setIsRefetching] = useState(false)
  const { trackSearch, trackCategoryChanged, trackRefreshClicked } =
    useConnectorsTracking()

  const {
    categories,
    connectorsGroup,
    connectors,
    allEssentialConnectorsEnabled,
    isLoading,
    isError,
    getCompanyConnectorByService,
    refetchConnectors,
    getConnectorById,
  } = useConnect()

  // Check if any connectors are in CONNECTING state
  const hasConnectingConnectors = useMemo(
    () => connectors.some(connector => connector.status === "PENDING"),
    [connectors],
  )

  // Update active category when URL category changes or categories are loaded
  useEffect(() => {
    if (categoryFromUrl && categories.includes(categoryFromUrl)) {
      setActiveCategory(categoryFromUrl)
    } else if (categoryFromUrl && categories.length > 0) {
      // If URL category doesn't exist, fall back to Essential or first available
      setActiveCategory(
        categories.includes("Essential") ? "Essential" : categories[0],
      )
    }
  }, [categoryFromUrl, categories])

  const canShowSubscribeDialog = useMemo(
    () =>
      currentCompany?.metadata?.isBillingEnabled &&
      allEssentialConnectorsEnabled &&
      !isSuperAdmin &&
      !currentCompany.subscription &&
      currentCompany.setupStatus === CompanySetupStatus.INITIAL_SETUP,
    [currentCompany, allEssentialConnectorsEnabled],
  )

  const closeDialog = () => {
    setDialog(null)
  }

  const showSuccessMessage = () => {
    toast.success("Connection was successfully established!", {
      toastId: `onboarding-success-${fivetranId}`,
    })
  }

  const showErrorMessage = () => {
    toast.error("Failed to complete the onboarding", {
      toastId: `onboarding-failed-${fivetranId}`,
    })
  }

  useEffect(() => {
    if (isCompleteAsyncError) {
      void showErrorMessage()
      void refetchConnectors()
    }
  }, [isCompleteAsyncError])

  useEffect(() => {
    const completeOnboarding = async (id: string) => {
      setIsLoading(true)
      try {
        const result = await completeAsync({
          completeOnboardingDto: { fivetranId: id },
        })

        if ("data" in result) {
          void showSuccessMessage()
          // completeOnboarding mutation already invalidates the CompanyConnectors
          // tag, so the shared query auto-refetches. No manual refetch needed.
        }
      } catch (error) {
        if (
          (isBadRequestError(error) ||
            isUnauthorizedError(error) ||
            isInternalServerError(error)) &&
          isCompleteAsyncError
        ) {
          void showErrorMessage()
          await refetchConnectors()
        }
      } finally {
        setIsLoading(false)
      }
    }

    if (fivetranId) {
      void completeOnboarding(fivetranId)
    }
  }, [fivetranId])

  const connectorsByCategory = useMemo(() => {
    const connectors = connectorsGroup[activeCategory] || []
    if (!searchTerm) {
      return connectors
    }

    return connectors.filter(c => c.name.toLowerCase().includes(searchTerm))
  }, [activeCategory, searchTerm, connectorsGroup])

  const showMetaAdvanced = useMemo(
    () =>
      shouldShowMetaAdvanced(connectorsGroup[activeCategory] || [], searchTerm),
    [activeCategory, searchTerm, connectorsGroup],
  )

  const openConfirmDialog = (id: string, service: string) => {
    const props = confirmationDialogPropsMap[service]
    if (props) {
      setDialog(() => (
        <ConfirmConnectionDialog
          isOpen
          companyConnectorId={id}
          onClose={closeDialog}
          {...props}
        />
      ))
    }
  }

  const openSuccessDialog = (companyConnectorId: string, service: string) => {
    const companyConnector = getCompanyConnectorByService(service)
    if (!companyConnector) {
      toast.error(`Oops... Seems like this service is not ready yet.`)
      return
    }

    const props = confirmationDialogPropsMap[service]
    if (props) {
      const details: Omit<typeof props, "title"> & { title?: string } = {
        ...props,
      }
      delete details.title
      setDialog(() => (
        <SuccessConnectionDialog
          title={`Your connection to ${details.label} is pending!`}
          isOpen
          onClose={closeDialog}
          companyConnectorId={companyConnectorId}
          {...details}
        />
      ))
    }
  }

  const handleSearch = (value: string) => {
    const normalizedSearchTerm = value.toLocaleLowerCase().trim()
    setSearchTerm(normalizedSearchTerm)
    if (normalizedSearchTerm) {
      trackSearch({
        search_term: normalizedSearchTerm,
        category: activeCategory,
      })
    }
  }

  const handleCategoryChange = (newCategory: string) => {
    trackCategoryChanged({
      category: newCategory,
      previous_category: activeCategory,
    })
    setActiveCategory(newCategory)

    // Update URL search parameters
    const newSearchParams = new URLSearchParams(searchParams)
    if (newCategory === "Essential") {
      // Remove category param for Essential (default)
      newSearchParams.delete("category")
    } else {
      newSearchParams.set("category", newCategory)
    }

    // Update URL without triggering navigation
    setSearchParams(newSearchParams, { replace: true })
  }

  const handleRefetch = async () => {
    trackRefreshClicked()
    setIsRefetching(true)
    try {
      await refetchConnectors()
    } finally {
      setIsRefetching(false)
    }
  }

  useEffect(() => {
    if (isError) {
      toast.error("Failed to load Data Connectors. Please, try again later", {
        toastId: "connectors-table-toast",
      })
    }
  }, [isError])

  useEffect(() => {
    if (canShowSubscribeDialog) {
      setShowSubscriptionDialog(true)
    }
  }, [canShowSubscribeDialog])

  useEffect(() => {
    const beginAction = (
      id: string,
      type: ConnectorTypeEnum = ConnectorTypeEnum.FIVETRAN,
    ) => {
      const companyConnector = connectors.find(c => c.id === id)
      if (companyConnector && type === ConnectorTypeEnum.CUSTOM) {
        void openConfirmDialog(companyConnector.id, companyConnector.service)
      } else if (companyConnector && type === ConnectorTypeEnum.FIVETRAN) {
        void openSuccessDialog(companyConnector.id, companyConnector.service)
      }

      void setStateParam(null)
      window.history.replaceState({}, "", `${location.pathname}`)
    }

    const connectorType = stateParam?.type
    const companyConnectorId = stateParam?.companyConnectorId
    if (companyConnectorId && connectors?.length) {
      void beginAction(companyConnectorId, connectorType)
    }
  }, [connectors])

  // Auto-open connector form when redirected from OAuth
  useEffect(() => {
    const autoOpenConnectorForm = () => {
      if (!autoOpenService || !connectors?.length) {
        return
      }

      // Find the company connector for this service
      const companyConnector = getCompanyConnectorByService(autoOpenService)
      if (!companyConnector) {
        return
      }

      // Get connector configuration to check if it's Airbyte
      const connector = getConnectorById(autoOpenService)
      if (!connector) {
        return
      }

      // Check if this is an Airbyte connector
      const isAirbyte = isAirbyteConnector(connector)

      if (isAirbyte) {
        // For Airbyte connectors, show the additional config form
        // Use the explicit companyConnectorId from the redirect URL when available
        // (critical for multi-connection where multiple connectors share the same service name)
        const targetConnectorId =
          autoOpenCompanyConnectorId ?? companyConnector.id
        try {
          // yotpo_airbyte has a bespoke dialog: it collects App Key + Secret
          // Key and the backend exchanges them for the access token (legacy
          // yotpo parity). The generic additional-config form would ask the
          // user to paste a raw access token instead.
          if (autoOpenService === CONNECTORS_IDS.YOTPO_AIRBYTE) {
            setDialog(() => (
              <YotpoAirbyteForm
                isOpen
                service={autoOpenService}
                companyConnectorId={targetConnectorId}
                onClose={() => setDialog(null)}
              />
            ))
          } else {
            setDialog(() => (
              <AdditionalConfigForm
                isOpen
                companyConnectorId={targetConnectorId}
                connector={connector}
                // OAuth-phase fields were asked before the consent screen
                configPhase="source"
                onClose={() => setDialog(null)}
              />
            ))
          }

          // Clean up the URL parameters after opening the form
          const newSearchParams = new URLSearchParams(searchParams)
          newSearchParams.delete("autoOpen")
          newSearchParams.delete("companyConnectorId")
          setSearchParams(newSearchParams, { replace: true })
        } catch (error) {
          toast.error(`Failed to open ${autoOpenService} setup form`, {
            toastId: `auto-open-${autoOpenService}-error`,
          })
        }
        return
      }

      // Custom (non-Airbyte) connectors that declare a customAdditionalConfig
      // on their service class (e.g. instagram_insights → pick IG account).
      if (CUSTOM_ADDITIONAL_CONFIG_SERVICES.has(autoOpenService)) {
        try {
          setDialog(() => (
            <CustomConnectorConfigForm
              isOpen
              companyConnectorId={companyConnector.id}
              connector={connector}
              onClose={() => setDialog(null)}
            />
          ))

          const newSearchParams = new URLSearchParams(searchParams)
          newSearchParams.delete("autoOpen")
          newSearchParams.delete("companyConnectorId")
          setSearchParams(newSearchParams, { replace: true })
        } catch (error) {
          toast.error(`Failed to open ${autoOpenService} setup form`, {
            toastId: `auto-open-${autoOpenService}-error`,
          })
        }
        return
      }

      // Check if this connector supports ingestion (automatic detection)
      if (isIngestionConnector(connector)) {
        try {
          setDialog(() => (
            <IngestionForm
              isOpen
              service={autoOpenService}
              companyConnectorId={companyConnector.id}
              onClose={() => setDialog(null)}
            />
          ))

          // Clean up the URL parameter after opening the form
          const newSearchParams = new URLSearchParams(searchParams)
          newSearchParams.delete("autoOpen")
          setSearchParams(newSearchParams, { replace: true })
        } catch (error) {
          toast.error(`Failed to open ${autoOpenService} ingestion form`, {
            toastId: `auto-open-ingestion-${autoOpenService}-error`,
          })
        }
        return
      }

      // Existing Fivetran connector logic
      const connectorService = serviceConnectorMap[autoOpenService]
      if (!connectorService) {
        return
      }

      // Check if we should show the form for the current connector status
      const currentStatus = companyConnector.status
      if (connectorService.showForStatuses) {
        if (!connectorService.showForStatuses.includes(currentStatus)) {
          // Don't auto-open if status isn't in the showForStatuses list
          return
        }
      }

      try {
        // Use the existing company connector instead of creating a new one
        const { component: ConnectorDialog } = connectorService

        setDialog(() => (
          <ConnectorDialog
            isOpen
            uri={undefined} // No URI needed for existing connectors
            service={autoOpenService}
            companyConnectorId={companyConnector.id}
            onClose={() => setDialog(null)}
          />
        ))

        // Clean up the URL parameter after opening the form
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.delete("autoOpen")
        setSearchParams(newSearchParams, { replace: true })
      } catch (error) {
        toast.error(`Failed to open ${autoOpenService} setup form`, {
          toastId: `auto-open-${autoOpenService}-error`,
        })
      }
    }

    void autoOpenConnectorForm()
  }, [
    autoOpenService,
    autoOpenCompanyConnectorId,
    connectors,
    getCompanyConnectorByService,
    setSearchParams,
    searchParams,
  ])

  const subscriptionDialog = showSubscriptionDialog ? (
    <SubscribeDialog onClose={() => setShowSubscriptionDialog(false)} />
  ) : null

  return (
    <div className="flex h-screen max-h-screen overflow-hidden">
      {/* Global polling manager for Airbyte connectors */}
      <AirbytePollingManager />

      {/* Sidebar */}
      <Sidebar
        value={activeCategory}
        categories={categories}
        onSelect={handleCategoryChange}
      />

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 px-4 py-6 md:px-6 md:py-8 overflow-y-auto">
          {/* Mobile category selector */}
          <div className="md:hidden mb-4">
            <label
              htmlFor="category-select"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Category
            </label>
            <select
              id="category-select"
              value={activeCategory}
              onChange={e => handleCategoryChange(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-basic-blue focus:border-basic-blue"
            >
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Data Sync Info - shown when connectors are syncing */}
          <DataSyncInfo isVisible={hasConnectingConnectors} />

          {/* Setup Status Banner - shown when integrations are not ready */}
          {!integrationsReady && setupMessage && (
            <div className="bg-blue-600 border border-blue-500 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <p className="text-white text-sm font-medium">{setupMessage}</p>
              </div>
            </div>
          )}

          <div className="space-y-6 ">
            {!isLoading ? (
              <SourceItems
                connectors={connectorsByCategory}
                activeCategory={activeCategory}
                onSearch={handleSearch}
                integrationsReady={integrationsReady}
                onRefetch={handleRefetch}
                isRefetching={isRefetching}
                showMetaAdvanced={showMetaAdvanced}
              />
            ) : (
              <div className="flex justify-center items-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dialogs */}
      {Dialog}
      {subscriptionDialog}
    </div>
  )
}

export default ConnectorsTable
